import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';

interface FantasyTeam {
  position: number;
  teamId: string;
  teamName: string;
  totalPoints: number;
  userId: string;
  userEmail?: string;
  userName?: string;
}

interface LeagueStandingsResponse {
  league: {
    id: string;
    name: string;
    type: 'public' | 'private';
    maxTeams?: number;
    teamsCount?: number;
  };
  standings: FantasyTeam[];
}

interface League {
  id: string;
  name: string;
  description?: string;
  type: 'public' | 'private';
  maxTeams?: number;
  teamsCount?: number;
}

interface LeagueRankingPageProps {
  leagueId: string;
  leagueName: string;
  onBack: () => void;
  onViewParticipantFechas?: (teamId: string, teamName: string) => void;
}

const positionStyle = (pos: number) => {
  if (pos === 1) return { color: '#FFB800', label: '🥇' };
  if (pos === 2) return { color: '#C0C0C0', label: '🥈' };
  if (pos === 3) return { color: '#CD7F32', label: '🥉' };
  return { color: 'rgba(255,255,255,0.35)', label: `${pos}` };
};

const LeagueRankingPage: React.FC<LeagueRankingPageProps> = ({ leagueId, leagueName, onBack, onViewParticipantFechas }) => {
  const [league, setLeague] = useState<League | null>(null);
  const [standings, setStandings] = useState<FantasyTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserTeamId] = useState<string | null>(localStorage.getItem('fantasyTeamId'));

  const fetchLeagueStandings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}/ranking`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        const standingsData: LeagueStandingsResponse = data.data;
        setLeague(standingsData.league);
        setStandings(standingsData.standings);
      } else {
        setError('Error al cargar el ranking de la liga');
      }
    } catch {
      setError('Error al cargar el ranking de la liga');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { fetchLeagueStandings(); }, [fetchLeagueStandings]);

  const myTeam = standings.find(t => t.teamId === currentUserTeamId);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 border-2 border-lub-blue border-t-transparent rounded-full animate-spin" />
        <p className="text-white/40 text-sm">Cargando ranking...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-4xl">⚠️</p>
        <p className="text-white/60 text-sm text-center">{error}</p>
        <button onClick={onBack} className="px-6 py-2.5 rounded-xl text-sm font-bold text-white"
          style={{ background: 'rgba(0,87,184,0.5)', border: '1px solid rgba(0,87,184,0.6)' }}>
          ← Volver
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-3 pt-2 pb-3 -mt-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-semibold mb-3 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
          Ligas
        </button>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Ranking</p>
            <h1 className="text-2xl font-black text-white leading-tight">{league?.name || leagueName}</h1>
          </div>
          <span className="mt-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{ background: league?.type === 'private' ? 'rgba(139,92,246,0.2)' : 'rgba(0,87,184,0.2)', color: league?.type === 'private' ? '#a78bfa' : '#60a5fa', border: `1px solid ${league?.type === 'private' ? 'rgba(139,92,246,0.3)' : 'rgba(0,87,184,0.3)'}` }}>
            {league?.type === 'private' ? '🔒 Privada' : '🌐 Pública'}
          </span>
        </div>
      </div>

      {/* My position banner */}
      {myTeam && (
        <div className="px-3 mb-3">
          <div className="rounded-2xl px-4 py-3 flex items-center gap-3"
            style={{ background: 'rgba(0,87,184,0.18)', border: '1px solid rgba(0,87,184,0.35)' }}>
            <div className="text-2xl font-black" style={{ color: positionStyle(myTeam.position).color }}>
              {myTeam.position <= 3 ? positionStyle(myTeam.position).label : `#${myTeam.position}`}
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm leading-tight">Tu equipo</p>
              <p className="text-white/50 text-xs">{myTeam.teamName}</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-black" style={{ color: '#4ade80' }}>{Math.round(myTeam.totalPoints)}</p>
              <p className="text-white/40 text-[10px] font-semibold uppercase">pts</p>
            </div>
          </div>
        </div>
      )}

      {/* Standings */}
      <div className="px-3">
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest">Clasificación</p>
          <p className="text-[10px] text-white/30">{standings.length} equipos</p>
        </div>

        {standings.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-4xl mb-3">🏀</p>
            <p className="text-white/60 text-sm">No hay equipos en esta liga todavía</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {standings.map(team => {
              const isMe = team.teamId === currentUserTeamId;
              const { color, label } = positionStyle(team.position);
              return (
                <div
                  key={team.teamId}
                  onClick={() => onViewParticipantFechas?.(team.teamId, team.teamName)}
                  className="rounded-2xl px-4 py-3 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
                  style={{
                    background: isMe ? 'rgba(0,87,184,0.18)' : 'rgba(255,255,255,0.05)',
                    border: isMe ? '1px solid rgba(0,87,184,0.40)' : '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  {/* Position */}
                  <div className="w-8 text-center flex-shrink-0">
                    {team.position <= 3 ? (
                      <span className="text-lg">{label}</span>
                    ) : (
                      <span className="text-sm font-black" style={{ color }}>{label}</span>
                    )}
                  </div>

                  {/* Team info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold leading-tight truncate ${isMe ? 'text-white' : 'text-white/90'}`}>
                      {team.teamName}
                      {isMe && <span className="ml-1.5 text-[10px] font-bold text-lub-blue bg-lub-blue/10 px-1.5 py-0.5 rounded-full">TÚ</span>}
                    </p>
                    {(team.userName || team.userEmail) && (
                      <p className="text-white/35 text-xs truncate mt-0.5">{team.userName || team.userEmail}</p>
                    )}
                  </div>

                  {/* Points */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-black" style={{ color: '#4ade80' }}>{Math.round(team.totalPoints || 0)}</p>
                    <p className="text-white/30 text-[10px] font-semibold uppercase">pts</p>
                  </div>

                  {/* Arrow */}
                  {onViewParticipantFechas && (
                    <svg className="w-4 h-4 text-white/20 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeagueRankingPage;
